import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import useAuth from '../../auth/useAuth';
import authApi from '../../api/authApi';
import { applyServerErrors, getErrorMessage } from '../../utils/errorHandler';

const profileSchema = yup.object({
  name: yup.string().trim().required('El nombre es obligatorio').max(255),
  phone: yup.string().nullable(),
});

const passwordSchema = yup.object({
  current_password: yup.string().required('Ingresa tu contraseña actual'),
  password: yup.string().min(8, 'Debe tener al menos 8 caracteres').required('La nueva contraseña es obligatoria'),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref('password')], 'Las contraseñas no coinciden')
    .required('Confirma la nueva contraseña'),
});

const emptyPasswordValues = { current_password: '', password: '', password_confirmation: '' };

// Perfil del propio usuario autenticado (administrador o tecnico): editar nombre/telefono
// y cambiar la contraseña. El correo no es editable aqui, es el identificador de login.
export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    reset: resetProfile,
    setError: setProfileError,
    formState: { errors: profileErrors, isSubmitting: isSavingProfile },
  } = useForm({
    resolver: yupResolver(profileSchema),
    defaultValues: { name: '', phone: '' },
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    setError: setPasswordError,
    formState: { errors: passwordErrors, isSubmitting: isSavingPassword },
  } = useForm({
    resolver: yupResolver(passwordSchema),
    defaultValues: emptyPasswordValues,
  });

  useEffect(() => {
    if (user) {
      resetProfile({ name: user.name || '', phone: user.phone || '' });
    }
  }, [user, resetProfile]);

  const onSubmitProfile = async (data) => {
    try {
      const response = await authApi.updateProfile(data);
      updateUser(response.data?.data || response.data);
      enqueueSnackbar('Perfil actualizado correctamente.', { variant: 'success' });
    } catch (error) {
      const handled = applyServerErrors(error, setProfileError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo actualizar el perfil.'), { variant: 'error' });
      }
    }
  };

  const onSubmitPassword = async (data) => {
    try {
      await authApi.updatePassword(data);
      resetPassword(emptyPasswordValues);
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      enqueueSnackbar('Contraseña actualizada correctamente.', { variant: 'success' });
    } catch (error) {
      const handled = applyServerErrors(error, setPasswordError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo actualizar la contraseña.'), { variant: 'error' });
      }
    }
  };

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4">Mi perfil</Typography>
        <Typography variant="body2" color="text.secondary">
          Datos de tu cuenta y cambio de contraseña.
        </Typography>
      </div>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Datos personales
              </Typography>
              <Stack
                component="form"
                spacing={2.5}
                sx={{ mt: 1 }}
                onSubmit={handleSubmitProfile(onSubmitProfile)}
                noValidate
              >
                <TextField label="Correo electrónico" fullWidth value={user?.email || ''} disabled />

                <TextField
                  label="Nombre completo"
                  fullWidth
                  error={Boolean(profileErrors.name)}
                  helperText={profileErrors.name?.message}
                  {...registerProfile('name')}
                />

                <TextField
                  label="Teléfono"
                  fullWidth
                  error={Boolean(profileErrors.phone)}
                  helperText={profileErrors.phone?.message}
                  {...registerProfile('phone')}
                />

                <Stack sx={{ justifyContent: 'flex-end' }} direction="row">
                  <Button type="submit" variant="contained" disabled={isSavingProfile}>
                    {isSavingProfile ? 'Guardando...' : 'Guardar cambios'}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cambiar contraseña
              </Typography>
              <Stack
                component="form"
                spacing={2.5}
                sx={{ mt: 1 }}
                onSubmit={handleSubmitPassword(onSubmitPassword)}
                noValidate
              >
                <TextField
                  label="Contraseña actual"
                  type={showCurrentPassword ? 'text' : 'password'}
                  fullWidth
                  error={Boolean(passwordErrors.current_password)}
                  helperText={passwordErrors.current_password?.message}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowCurrentPassword((s) => !s)} edge="end">
                            {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  {...registerPassword('current_password')}
                />

                <TextField
                  label="Nueva contraseña"
                  type={showNewPassword ? 'text' : 'password'}
                  fullWidth
                  error={Boolean(passwordErrors.password)}
                  helperText={passwordErrors.password?.message}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowNewPassword((s) => !s)} edge="end">
                            {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  {...registerPassword('password')}
                />

                <TextField
                  label="Confirmar nueva contraseña"
                  type={showNewPassword ? 'text' : 'password'}
                  fullWidth
                  error={Boolean(passwordErrors.password_confirmation)}
                  helperText={passwordErrors.password_confirmation?.message}
                  {...registerPassword('password_confirmation')}
                />

                <Stack sx={{ justifyContent: 'flex-end' }} direction="row">
                  <Button type="submit" variant="contained" disabled={isSavingPassword}>
                    {isSavingPassword ? 'Actualizando...' : 'Actualizar contraseña'}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
